import { intensityForLevel, type LevelIntensity } from '../generation/curve';
import { difficultyLabelForLevel, type DifficultyLabel } from './difficultyLabel';
import type { LevelClearDetail } from './events';
import { maximumLevelNumber } from './levels';

export interface TrackEntry {
  readonly levelNumber: number;
  /** 表示する難易度の段位はすべてここから導く。派生値は持たない。 */
  readonly intensity: LevelIntensity;
  readonly state: 'current' | 'next' | 'upcoming';
}

export interface ResultViewModel {
  readonly levelNumber: number;
  readonly arrowTotal: number;
  readonly praiseText: string;
  readonly timeText: string;
  readonly difficultyLabel: DifficultyLabel;
  readonly cleanStreak: number;
  readonly isFinalLevel: boolean;
  readonly track: readonly TrackEntry[];
}

const praiseTextForIntensity: Readonly<Record<LevelIntensity, (arrowTotal: number) => string>> = {
  normal: (arrowTotal) => `${arrowTotal} 本の矢印を、すべて外に出しました。`,
  hard: (arrowTotal) => `お見事！ハード ${arrowTotal} 本をクリア！`,
  superhard: (arrowTotal) => `スーパーハード制覇！${arrowTotal} 本すべて外に出しました！`,
};

/**
 * 結果画面のトラックは「今クリアしたレベル + この先」の前方視界。
 * 窓には送り出したあとの 5 段（= 次レベルから 5 つ）が収まる。
 */
const trackLookahead = 5;

export function formatElapsedTime(elapsedMs: number): string {
  const totalSeconds = Math.floor(Math.max(0, elapsedMs) / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function buildResultViewModel(
  detail: LevelClearDetail,
  progress: { highestClearedLevel: number; cleanStreak: number },
): ResultViewModel {
  const intensity = intensityForLevel(detail.levelNumber);
  const trackEnd = Math.min(maximumLevelNumber, detail.levelNumber + trackLookahead);
  const track = Array.from({ length: trackEnd - detail.levelNumber + 1 }, (_, index): TrackEntry => {
    const levelNumber = detail.levelNumber + index;
    return {
      levelNumber,
      intensity: intensityForLevel(levelNumber),
      state: index === 0 ? 'current' : index === 1 ? 'next' : 'upcoming',
    };
  });

  return {
    levelNumber: detail.levelNumber,
    arrowTotal: detail.arrowTotal,
    praiseText: praiseTextForIntensity[intensity](detail.arrowTotal),
    timeText: formatElapsedTime(detail.elapsedMs),
    difficultyLabel: difficultyLabelForLevel(detail.levelNumber),
    cleanStreak: progress.cleanStreak,
    isFinalLevel: detail.levelNumber === maximumLevelNumber,
    track,
  };
}
